/**
 * SitePick front — 一覧カード / 商品詳細のカラー切替（`parts/color-pick.php`）。docs/07 §13。
 *
 *  - カード: サムネを色別の画像（デモの assets/sitepick-colors/{key}.webp）に差し替える。標準に戻すと元の画像（srcset 付き）に戻す。
 *  - 詳細: デバイス枠（PC / SP）の iframe へ色を伝える（実物確認画面と同じ postMessage。live-colors.js）。
 *    枠は後から読み込まれるので、デモの「準備できた」（ready）にも今の色を返す。
 * 選んだ色は商品ごとに残し（color-store.js）、詳細・実物確認画面を開いたときにも同じ色で見せる。
 * 一覧の絞り込みで中身が差し替わっても、クリックは document で受けるのでそのまま動く。価格・在庫には関わらない。
 */

import { previewColor } from "./analytics.js";
import { storedColor, storeColor } from "./color-store.js";

/** 詳細ページで今見せている色（デバイス枠が後から読み込まれたときに返す）。 */
let detailKey = "base";

function deviceFrames() {
  return Array.from(document.querySelectorAll("[data-sp-devices] .c-device-shot__iframe")).filter(
    (el) => el instanceof HTMLIFrameElement
  );
}

/**
 * @param {Window|null} win
 * @param {string} key
 */
function sendTo(win, key) {
  if (win) {
    // sandbox の iframe は opaque origin なので targetOrigin は指定できない。送るのは案の key だけ。
    win.postMessage({ type: "sitepick:demo-color:set", key }, "*");
  }
}

/**
 * @param {HTMLElement} pick `.js-sp-color-pick`
 * @returns {string}
 */
function itemIdOf(pick) {
  const holder = pick.closest("[data-sp-item]");
  if (!holder) {
    return "";
  }
  try {
    const parsed = JSON.parse(holder.getAttribute("data-sp-item") || "{}");
    return parsed && parsed.item_id ? String(parsed.item_id) : "";
  } catch {
    return "";
  }
}

/**
 * カードのサムネを差し替える。元の src / srcset / sizes は最初に退避しておき、標準で戻す。
 *
 * @param {HTMLElement} pick
 * @param {HTMLButtonElement} btn
 */
function swapCardImage(pick, btn) {
  const card = pick.closest(".c-site-card");
  const img = card ? card.querySelector("img.c-site-card__img") : null;
  if (!(img instanceof HTMLImageElement)) {
    return;
  }
  if (!img.hasAttribute("data-sp-color-src")) {
    img.setAttribute("data-sp-color-src", img.getAttribute("src") || "");
    img.setAttribute("data-sp-color-srcset", img.getAttribute("srcset") || "");
    img.setAttribute("data-sp-color-sizes", img.getAttribute("sizes") || "");
  }
  const thumb = btn.getAttribute("data-sp-color-thumb") || "";
  if (thumb) {
    img.removeAttribute("srcset");
    img.removeAttribute("sizes");
    img.setAttribute("src", thumb);
    return;
  }
  const srcset = img.getAttribute("data-sp-color-srcset") || "";
  const sizes = img.getAttribute("data-sp-color-sizes") || "";
  if (srcset) {
    img.setAttribute("srcset", srcset);
  }
  if (sizes) {
    img.setAttribute("sizes", sizes);
  }
  img.setAttribute("src", img.getAttribute("data-sp-color-src") || "");
}

/**
 * 詳細のデバイス枠へ色を伝える（読み込み前の枠には、読み込み後の ready に返す）。
 *
 * @param {string} key
 */
function tellDevices(key) {
  detailKey = key;
  deviceFrames().forEach((iframe) => sendTo(iframe.contentWindow, key));
}

/**
 * @param {HTMLElement} pick
 * @param {string} key
 * @returns {boolean} 切り替えたか
 */
function apply(pick, key) {
  const btn = pick.querySelector(`.js-sp-color-btn[data-sp-color="${key}"]`);
  if (!(btn instanceof HTMLButtonElement)) {
    return false;
  }
  pick.querySelectorAll(".js-sp-color-btn").forEach((b) => {
    const active = b === btn;
    b.classList.toggle("is-active", active);
    b.setAttribute("aria-pressed", active ? "true" : "false");
  });
  const name = pick.querySelector("[data-sp-color-name]");
  if (name) {
    name.textContent = btn.getAttribute("data-sp-color-label") || "";
  }
  if (pick.getAttribute("data-sp-color-context") === "detail") {
    tellDevices(key);
  } else {
    swapCardImage(pick, btn);
  }
  return true;
}

/** 前に選んだ色で見せる（初期表示・絞り込みで中身が差し替わったとき）。計測はしない。 */
function restore() {
  document.querySelectorAll(".js-sp-color-pick").forEach((pick) => {
    const key = storedColor(pick.getAttribute("data-sp-color-id") || "");
    if (key && key !== "base") {
      apply(pick, key);
    }
  });
}

/**
 * @returns {void}
 */
export function initSiteColors() {
  // 絞り込みで色付きの商品が後から入ってくることがあるので、ボタンが無いページでも document で受ける。
  document.addEventListener("click", (e) => {
    const btn = e.target instanceof Element ? e.target.closest(".js-sp-color-btn") : null;
    const pick = btn ? btn.closest(".js-sp-color-pick") : null;
    if (!btn || !pick) {
      return;
    }
    const key = btn.getAttribute("data-sp-color") || "base";
    if (btn.getAttribute("aria-pressed") === "true" || !apply(pick, key)) {
      return;
    }
    storeColor(pick.getAttribute("data-sp-color-id") || "", key);
    previewColor(itemIdOf(pick), key, pick.getAttribute("data-sp-color-context") || "card");
  });

  // 色別サムネを先に取っておく（押してから空白にならないように）。カードに触れたときだけ。
  document.addEventListener(
    "pointerover",
    (e) => {
      const pick = e.target instanceof Element ? e.target.closest(".js-sp-color-pick") : null;
      if (!pick || pick.hasAttribute("data-sp-color-preloaded")) {
        return;
      }
      pick.setAttribute("data-sp-color-preloaded", "");
      pick.querySelectorAll("[data-sp-color-thumb]").forEach((b) => {
        const img = new Image();
        img.src = b.getAttribute("data-sp-color-thumb") || "";
      });
    },
    { passive: true }
  );

  // デバイス枠のデモが読み込まれたら（ready）、今の色を返す。標準のままなら何もしない。
  window.addEventListener("message", (e) => {
    const d = e.data;
    if (!d || d.type !== "sitepick:demo-color:ready" || detailKey === "base") {
      return;
    }
    const frame = deviceFrames().find((iframe) => iframe.contentWindow === e.source);
    if (frame && d.current !== detailKey) {
      sendTo(frame.contentWindow, detailKey);
    }
  });

  restore();
  document.addEventListener("sitepick:content:updated", restore);
}
